import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector } from "../../core/useDocSelector";
import { readTask } from "../../core/tasks";
import { setNotes } from "../../core/ops";
import { setSetting } from "../../app/settings";
import { C } from "../../core/schema";
import { Textarea } from "../../ui";

export interface NotesConfig {
  /** a task id, or "scratch" for the workspace scratch pad */
  taskId?: string;
}

/** Markdown notes pane (spec §9.7 `notes`): a task's notes, or the scratch pad. Plain textarea by design (§2). */
export function NotesView({ config, ctx }: ViewProps<NotesConfig>) {
  const { t } = useTranslation();
  const target = config.taskId && config.taskId !== "scratch" ? config.taskId : null;
  const value =
    useDocSelector(
      ctx.handle,
      (doc) =>
        target ? (readTask(doc, target)?.notes ?? "") : String(doc.getMap(C.settings).get("scratch") ?? ""),
      [target],
    ) ?? "";
  const title = useDocSelector(ctx.handle, (doc) => (target ? (readTask(doc, target)?.title ?? "") : ""), [
    target,
  ]);
  const [draft, setDraft] = useState<string | null>(null);
  const commit = useMemo(
    () => (text: string) =>
      ctx.write((doc) => (target ? setNotes(doc, target, text) : setSetting(doc, "scratch", text))),
    [ctx, target],
  );
  useEffect(() => {
    if (draft === null) return;
    const h = setTimeout(() => {
      commit(draft);
      setDraft(null);
    }, 500);
    return () => clearTimeout(h);
  }, [draft, commit]);
  return (
    <div className="flex h-full flex-col gap-1.5" data-testid="notes-view">
      <h3 className="px-1 text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
        {target ? title || t("task.untitled") : t("notes.scratch")}
      </h3>
      <Textarea
        className="min-h-0 flex-1 resize-none font-mono text-[13px]"
        aria-label={target ? t("task.notes") : t("notes.scratch")}
        placeholder={t("notes.placeholder")}
        value={draft ?? value}
        disabled={ctx.readOnly}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== null) {
            commit(draft);
            setDraft(null);
          }
        }}
      />
    </div>
  );
}

registerViewType<NotesConfig>({
  id: "notes",
  label: "views.notes.label",
  icon: "file-text",
  level: "standard",
  defaultConfig: { taskId: "scratch" },
  configSchema: { type: "object", properties: { taskId: { type: "string", title: "config.notesTask" } } },
  Component: NotesView,
  minSize: { w: 3, h: 3 },
});
